// Events
import { onQuestionAnswered, onStateFieldAnswered, onVehicleConfigSet, onVehicleSet, onVehicleValidVinSet } from '../actions/actions.answers';
import { HierarchicalData, ReplacementVehicleOptions } from '../interfaces/interface.lookup';
import { ReplacementVehicleSelection, Vehicle } from '../interfaces/interface.vehicle';
import EventBus from '../services/service.eventBus';
// Util
import { fetchVoiVinData } from '../services/service.vehicle';
import { state as customerState } from '../store/store.customer';
import { state as supplementState } from '../store/store.supplement';
import { ANSWER_LEAD_TYPE_CASH_OFFER, ANSWER_LEAD_TYPE_OPT_IN, ANSWER_LEAD_TYPE_OPT_OUT } from '../utils/utils.answers';
import { GUID_EMPTY } from '../utils/utils.interactive-banner';
import { hasEmptyValues, pick } from '../utils/utils.js';
import { formatPhoneNumber } from '../utils/utils.phone-format';
import {
  QUESTION_BROWSER_TYPE,
  QUESTION_CERTIFIED_CARS,
  QUESTION_INCOMING_URL,
  QUESTION_LEAD_TYPE,
  QUESTION_LNX_CODE,
  QUESTION_MAKE_MODEL,
  QUESTION_NEW_CARS,
  QUESTION_OFFER_CODE,
  QUESTION_PIXALL_ID,
  QUESTION_PIXALL_REAL_ID,
  QUESTION_SITE_REFERRAL_USER_ID,
  QUESTION_USED_CARS,
  QUESTION_YEAR_RANGE,
} from '../utils/utils.question';

const onOfferLoad = ({ contactInfo }) => {
  const { email, firstName, lastName, phone, zip } = contactInfo;

  customerState.email = email;
  customerState.firstName = firstName;
  customerState.lastName = lastName;
  if (phone) {
    customerState.phone = formatPhoneNumber(phone);
  }

  customerState.zip = zip;

  EventBus.publish('OfferLoadCustomer', { email, firstName, lastName, phone, zip });
};

export const onContactHydratorInit = (contactProps, hostIntegrationProps) => {
  const { firstName, lastName, phone, email, zip, emailCustomerCheckbox } = contactProps;
  // Update answers store with contact info props
  if (firstName !== '') {
    onStateFieldAnswered('contactInfo', 'firstName', firstName);
  }
  if (lastName !== '') {
    onStateFieldAnswered('contactInfo', 'lastName', lastName);
  }
  if (phone !== '') {
    onStateFieldAnswered('contactInfo', 'phone', phone);
  }
  if (email !== '') {
    onStateFieldAnswered('contactInfo', 'email', email);
  }
  if (zip !== '') {
    onStateFieldAnswered('contactInfo', 'zip', zip);
  }
  onStateFieldAnswered('contactInfo', 'emailCustomerCheckbox', emailCustomerCheckbox);

  setContactHostIntegrationAnswers(hostIntegrationProps);
};

const setContactHostIntegrationAnswers = (hostIntegrationProps) => {
  const { referrerUrl, includePixall, pixallId, pixallRealId, browserType, offerCode, lnxCode, dealerGuid, cobrandingGroupId } = hostIntegrationProps;

  // These are implemented as questions, so pass the values there so they will get to the answers store
  onStateFieldAnswered('pixall', 'includePixall', includePixall);
  onQuestionAnswered(QUESTION_INCOMING_URL, referrerUrl);
  onQuestionAnswered(QUESTION_PIXALL_ID, pixallId);
  onQuestionAnswered(QUESTION_PIXALL_REAL_ID, pixallRealId);
  onQuestionAnswered(QUESTION_BROWSER_TYPE, browserType);
  onQuestionAnswered(QUESTION_OFFER_CODE, offerCode);
  onQuestionAnswered(QUESTION_LNX_CODE, lnxCode);
  onQuestionAnswered(QUESTION_SITE_REFERRAL_USER_ID, dealerGuid);
  onStateFieldAnswered('dealerInfo', 'cobrandingGroupId', cobrandingGroupId);
};

// TODO: CONTACT COMPONENT (HERE) IS NOT THE RIGHT PLACE TO WRITE VEHICLE ANSWERS
// THIS SHOULD ONLY BE USED IF WE HAVE NO OTHER WAY TO WRITE VEHICLE INFORMATION
// EX: ON KBB.COM WHERE THEY DO NOT INTEGRATE OUR VEHICLE CONFIG OR ENTRY COMPONENTS
// ideally, we would deprecate this and write the vehicle + config in the "right" place
export const onContactVehicleSave = (vehicle: Vehicle, selectedOptionIds, unselectedOptionIds) => {
  // Write vehicle state if any of the vehicle props are set
  // this is set OPTIONAL to avoid writing vehicle multiple times (i.e. once in entry, again here)
  const baseVehicle = pick(vehicle, ['yearId', 'makeId', 'modelId', 'trimId', 'mileage']);
  if (!hasEmptyValues(baseVehicle)) {
    setVehicleAnswers(vehicle);
  }

  // Write vehicle configuration if any config value is provided as props
  // this is set OPTIONAL to avoid writing config multiple times (i.e. once in config, again here)
  const config = pick(vehicle, ['transmissionId', 'engineId', 'drivetrainId', 'colorId']);
  if (!hasEmptyValues(config)) {
    setVehicleConfigAnswers(vehicle, selectedOptionIds, unselectedOptionIds);
  }
};

const setVehicleAnswers = (vehicle) => {
  onVehicleSet(vehicle);
  onStateFieldAnswered('vehicle', 'mileage', vehicle.mileage);

  if (vehicle.vin?.trim().length > 0) {
    // Do we want to assume the incoming VIN is valid?
    // If not we'd need a separate flow to handle that. I think we'd likely want a separate component to do that or add something to contact-vehicle-compare
    // Possibly different messaging since the existing messaging is all around "VIN you entered" and there would be two vins in this case.
    onVehicleValidVinSet(vehicle.vin);
  }
};

const setVehicleConfigAnswers = (vehicle, selectedOptionIds, unselectedOptionIds) => {
  onVehicleConfigSet(vehicle);
  if (selectedOptionIds) {
    onStateFieldAnswered('vehicle', 'apiSelectedOptionIds', cleanOptionString(selectedOptionIds));
  }
  if (unselectedOptionIds) {
    onStateFieldAnswered('vehicle', 'apiUnselectedOptionIds', cleanOptionString(unselectedOptionIds));
  }
};

const cleanOptionString = (optionString = '') =>
  optionString
    .split(',')
    .filter((o) => !!o)
    .map((o) => parseFloat(o));

export const onReciveReplacementVehicleMakeModels = (replacementVehicleOptions: ReplacementVehicleOptions) => {
  supplementState.replacementVehicleOptions = replacementVehicleOptions;

  const { replacementVehicleTypes } = replacementVehicleOptions;

  if (replacementVehicleTypes && replacementVehicleTypes.length) {
    supplementState.rvLookingForOptions = supplementState.rvLookingForOptions.filter((item) => replacementVehicleTypes.includes(item.qRef));
  }
};

export const checkShowReplacementVehicle = (offerCode, acceptsRv) => {
  let showReplacementVehicleSection = true;
  if ((offerCode && offerCode.toUpperCase() === 'D') || !acceptsRv) {
    showReplacementVehicleSection = false;
  }
  customerState.showReplacementVehicleSection = showReplacementVehicleSection;
  return showReplacementVehicleSection;
};

export const onRVAnswerChange = (notifyReplacementVehicleAnswerChange, replacementVehicle, makesModels) => {
  if (notifyReplacementVehicleAnswerChange) {
    const leadType = replacementVehicle[QUESTION_LEAD_TYPE]?.value;
    const isLookingForCertifiedCars = replacementVehicle[QUESTION_CERTIFIED_CARS]?.value;
    const isLookingForUsedCars = replacementVehicle[QUESTION_USED_CARS]?.value;
    const isLookingForNewCars = replacementVehicle[QUESTION_NEW_CARS]?.value;
    const yearRange = replacementVehicle[QUESTION_YEAR_RANGE]?.value;
    const makeModelId = replacementVehicle[QUESTION_MAKE_MODEL]?.value;
    const makeModelRegex = /(mm\/\d+)(\/\d+)?/;
    let makeId;
    let selectedModel;
    let selectedMake;

    if (makeModelId) {
      const match = makeModelId.match(makeModelRegex);
      if (match) {
        makeId = match[1];
        selectedMake = makesModels?.find((p) => p.id === makeId);
        selectedModel = selectedMake?.values?.find((p) => p.id === replacementVehicle[QUESTION_MAKE_MODEL]?.value);
      }
    }
    const replacementVehicleSelectionData: ReplacementVehicleSelection = {
      leadType,
      isLookingForCertifiedCars,
      isLookingForUsedCars,
      isLookingForNewCars,
      yearRange,
      makeModelId,
      selectedMake,
      selectedModel,
    };
    EventBus.publish('ReplacementVehicleInfoChanged', replacementVehicleSelectionData);
  }
};

export const onFetchVoiVinData = async (voiVin, makesModels: HierarchicalData[], acceptsRv, dealerGuid) => {
  if (!voiVin) {
    return;
  }
  if (!acceptsRv) {
    const leadType = dealerGuid && dealerGuid !== GUID_EMPTY ? ANSWER_LEAD_TYPE_OPT_OUT : ANSWER_LEAD_TYPE_CASH_OFFER;
    onQuestionAnswered(QUESTION_LEAD_TYPE, leadType);
    return;
  }
  try {
    const voiVinData = await fetchVoiVinData(voiVin);
    onFetchVoiVinDataSuccess(voiVinData, makesModels);
  } catch (error) {
    onFetchVoiVinDataFail(error);
  }
};

const onFetchVoiVinDataSuccess = (voiVinData, makesModels) => {
  onQuestionAnswered(QUESTION_LEAD_TYPE, ANSWER_LEAD_TYPE_OPT_IN);
  onQuestionAnswered(QUESTION_NEW_CARS, voiVinData.isNewCar);
  onQuestionAnswered(QUESTION_USED_CARS, !voiVinData.isNewCar);

  const yearRange = `${voiVinData.yearId},${voiVinData.yearId}`;
  onQuestionAnswered(QUESTION_YEAR_RANGE, yearRange);

  const models = makesModels.find((p) => p.label === voiVinData.makeName);
  const makeModelId = models?.values?.find((p) => p.label === voiVinData.modelName)?.id;
  if (makeModelId) {
    onQuestionAnswered(QUESTION_MAKE_MODEL, makeModelId);
  }
  EventBus.publish('FetchVoiVinDataSuccess', voiVinData);
};

const onFetchVoiVinDataFail = (error) => {
  EventBus.publish('FetchVoiVinDataFail', error);
};

const actions = { onOfferLoad, onContactVehicleSave, onReciveReplacementVehicleMakeModels };

export { actions };
export default actions;
